import React, {useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import {CalendarExportButton, createCalendarDateTime} from '../../utils/calendarExport'

const API_URL = process.env.REACT_APP_API_URL
const GET_SCHEDULE_URL = `${API_URL}/Custom/GetStudentSchedule`

interface ScheduleItem {
  id: number
  day: number
  startTime: string
  endTime: string
  courseName: string
  courseCode?: string
  classroomName: string
  departmentName?: string
  name?: string
  detail?: string
  isMakeup?: boolean
  date?: string
}

const toMinutes = (time: string) => {
  const [h, m] = time.split(':').map(Number)
  return h * 60 + m
}

const formatDateKey = (date: Date) => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const StudentWeeklySchedule: React.FC = () => {
  const [schedule, setSchedule] = useState<ScheduleItem[]>([])
  const [semesterStart, setSemesterStart] = useState<Date | null>(null)
  const [semesterEnd, setSemesterEnd] = useState<Date | null>(null)
  const [weekIndex, setWeekIndex] = useState(0)
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 576)

  const COLORS = [
    '#FFCDD2',
    '#F8BBD0',
    '#E1BEE7',
    '#D1C4E9',
    '#C5CAE9',
    '#BBDEFB',
    '#B3E5FC',
    '#B2EBF2',
    '#B2DFDB',
    '#C8E6C9',
    '#DCEDC8',
    '#F0F4C3',
    '#FFF9C4',
    '#FFECB3',
    '#FFE0B2',
    '#FFCCBC',
    '#D7CCC8',
    '#F5F5F5',
    '#CFD8DC',
  ]

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 576)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    axios
      .get(GET_SCHEDULE_URL)
      .then((res) => {
        setSchedule(res.data.items || [])
        setSemesterStart(res.data.semesterStart ? new Date(res.data.semesterStart) : null)
        setSemesterEnd(res.data.semesterEnd ? new Date(res.data.semesterEnd) : null)
      })
      .catch(console.error)
  }, [])

  const days = useMemo(() => {
    const arr: Date[] = []
    if (semesterStart && semesterEnd) {
      const d = new Date(semesterStart)
      while (d <= semesterEnd) {
        arr.push(new Date(d))
        d.setDate(d.getDate() + 1)
      }
    }
    return arr
  }, [semesterStart, semesterEnd])

  const weeks = useMemo(() => {
    const result: Date[][] = []
    let week: Date[] = []
    days.forEach((date, idx) => {
      week.push(date)
      const isLast = idx === days.length - 1
      if (date.getDay() === 0 || isLast) {
        result.push(week)
        week = []
      }
    })
    return result
  }, [days])

  useEffect(() => {
    if (!weeks.length) {
      setWeekIndex(0)
      return
    }

    const today = new Date()
    const todayKey = formatDateKey(today)
    const currentWeekIndex = weeks.findIndex((week) =>
      week.some((date) => formatDateKey(date) === todayKey)
    )

    setWeekIndex(currentWeekIndex >= 0 ? currentWeekIndex : 0)
  }, [weeks])

  const itemsByDate = useMemo(() => {
    const map: Record<string, ScheduleItem[]> = {}
    days.forEach((date) => {
      const key = formatDateKey(date)
      const dow = date.getDay() === 0 ? 7 : date.getDay()
      const dayItems = schedule
        .filter((item) => {
          if (item.date) {
            return formatDateKey(new Date(item.date)) === key
          }
          return item.day === dow
        })
        .sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime))
      map[key] = dayItems
    })
    return map
  }, [days, schedule])

  const currentWeek = weeks[weekIndex] || []

  const weekDays = useMemo(() => {
    return currentWeek.filter((date) => {
      const key = formatDateKey(date)
      const items = itemsByDate[key] || []
      const dow = date.getDay()
      if (items.length === 0 && (dow === 0 || dow === 6)) {
        return false
      }
      return true
    })
  }, [currentWeek, itemsByDate])

  const courseColors = useMemo(() => {
    const map = new Map<string, string>()
    let i = 0
    schedule.forEach((item) => {
      const key = item.courseCode || item.courseName
      if (!map.has(key)) {
        map.set(key, COLORS[i % COLORS.length])
        i++
      }
    })
    return map
  }, [schedule])

  const calendarEvents = useMemo(() => {
    return days.flatMap((date) => {
      const key = formatDateKey(date)
      return (itemsByDate[key] || []).map((item) => ({
        uid: `student-course-${item.id}-${key}-${item.startTime}`,
        title: item.courseCode ? `${item.courseName} (${item.courseCode})` : item.courseName,
        start: createCalendarDateTime(key, item.startTime),
        end: createCalendarDateTime(key, item.endTime),
        location: item.classroomName,
        description: [
          item.departmentName,
          item.name?.split(' - ').pop(),
          item.detail,
          item.isMakeup ? 'Telafi' : null,
        ]
          .filter(Boolean)
          .join('\n'),
      }))
    })
  }, [days, itemsByDate])

  return (
    <div className='container mt-4'>
      <div className='d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4'>
        <h2 className='mb-0'>Ders Programım</h2>
        <CalendarExportButton events={calendarEvents} fileName='ders-programim' />
      </div>
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <button
          className='btn btn-light btn-sm'
          disabled={weekIndex === 0}
          onClick={() => setWeekIndex((w) => w - 1)}
        >
          Önceki Hafta
        </button>
        <span className='fw-bold'>{weekIndex + 1}. Hafta</span>
        <button
          className='btn btn-light btn-sm'
          disabled={weekIndex >= weeks.length - 1}
          onClick={() => setWeekIndex((w) => w + 1)}
        >
          Sonraki Hafta
        </button>
      </div>
      <div className='d-flex flex-column flex-sm-row'>
        {weekDays.map((date) => {
          const key = formatDateKey(date)
          const items = itemsByDate[key] || []
          const courseGroups = Array.from(
            items.reduce((acc, item) => {
              const ck = item.courseCode || item.courseName
              if (!acc.has(ck)) acc.set(ck, [] as ScheduleItem[])
              acc.get(ck)!.push(item)
              return acc
            }, new Map<string, ScheduleItem[]>())
          )
          const dayWidth = isMobile ? '100%' : `${100 / weekDays.length}%`
          return (
            <div
              key={key}
              className='border border-2 rounded me-sm-2 mb-2 mb-sm-0 flex-shrink-0'
              style={{width: dayWidth}}
            >
              <div className='bg-light text-center fw-bold p-2'>
                {date.toLocaleDateString('tr-TR', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'numeric',
                })}
              </div>
              {courseGroups.length > 0 ? (
                courseGroups.map(([ck, items]) => {
                  const color = courseColors.get(ck) || '#ccc'
                  const isMakeup = items.some((i) => i.isMakeup)
                  return (
                    <div key={ck} className='p-2 border-top'>
                      <div
                        className='p-2 rounded border'
                        style={{borderColor: color, backgroundColor: `${color}33`}}
                      >
                        <div className='fw-semibold'>
                          {items[0].courseCode
                            ? `${items[0].courseName} (${items[0].courseCode})`
                            : items[0].courseName}
                          {isMakeup && (
                            <span className='badge bg-warning text-dark ms-1'>Telafi</span>
                          )}
                        </div>
                        {items.map((g, idx) => (
                          <div key={idx} className='text-muted small'>
                            {g.startTime} - {g.endTime} {g.classroomName}
                            {g.departmentName && ` - ${g.departmentName}`}
                            {g.name && ` - ${g.name.split(' - ').pop()}`}
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })
              ) : (
                <div className='p-2 text-muted'>Ders yok</div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default StudentWeeklySchedule
